import React from 'react';
import { Button, Icon, Input, Link, Modal } from '../../../../../shared/ui';
import { IconMap } from '../../../../../shared/sprite';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { gongModel } from '../../../../../entities/gong';

type Props = {
  isOpen: boolean;
  onClose(): void;
};

type GongFormSchema = {
  accessKey: string;
  accessKeySecret: string;
};

const gongFormInputSchema = object().shape({
  accessKey: string().required('Please enter Gong Access Key'),
  accessKeySecret: string().required('Please enter Gong Access Key Secret'),
});

export const GongIntegrationModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const { register, handleSubmit } = useForm<GongFormSchema>({
    resolver: yupResolver(gongFormInputSchema),
  });

  const isCreatingGongTeamSecrets = useSelector(
    gongModel.selectors.selectIsCreatingTeamSecrets
  );

  const onSubmit = handleSubmit(({ accessKey, accessKeySecret }) => {
    dispatch(
      gongModel.actions.createGongTeamSecrets({
        accessKey,
        accessKeySecret,
        cb: onClose,
      })
    );
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isShowCancelButton={false}
      className="w-160"
    >
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex items-start justify-between w-full">
          <Icon glyph={IconMap.GongLg} width={48} />
          <Button
            color="transparent"
            variant="icon"
            onClick={onClose}
            size="sm"
          >
            <Icon glyph={IconMap.XClose} width={24} className="text-gray-400" />
          </Button>
        </div>

        <div className="text-display-xs font-display text-gray-900 font-medium">
          Gong integration
        </div>
      </div>
      <form onSubmit={onSubmit}>
        <p className="text-sm text-gray-600 mb-5">
          Connect your Gong account to Distribute. All team members will be able
          to use call transcripts.
        </p>
        <p className="text-sm text-gray-600">Simply follow these steps:</p>
        <ol className="text-sm text-gray-600 list-decimal pl-4 py-3">
          <li className="mb-2">
            Go to{' '}
            <Link
              target="_blank"
              rel="noreferrer"
              href={'https://app.gong.io/company/api'}
              size="medium"
              className="inline-flex gap-x-0.5 items-center"
            >
              Gong API Page
              <Icon glyph={IconMap.ArrowSquareUpRight} width={16} />
            </Link>{' '}
            (you must be a technical administrator in Gong).
          </li>
          <li>
            Click "Create" to receive an{' '}
            <span className="font-semibold text-gray-900">Access Key</span> and
            an{' '}
            <span className="font-semibold text-gray-900">
              Access Key Secret
            </span>
            .
          </li>
        </ol>
        <div className="w-100 flex flex-col gap-3 mb-5">
          <Input
            type="text"
            placeholder="Access Key"
            autoComplete="off"
            heightSize="md"
            {...register('accessKey')}
          />
          <Input
            type="password"
            placeholder="Access Key Secret"
            autoComplete="new-password"
            heightSize="md"
            {...register('accessKeySecret')}
          />
        </div>
        <Button
          type="submit"
          variant="text"
          color="primary"
          className="w-25"
          loading={isCreatingGongTeamSecrets}
        >
          Connect
        </Button>
      </form>
    </Modal>
  );
};
